/**
 * The app's own record of whether the user chose to connect.
 *
 * WHY THIS EXISTS. wagmi reconnects on mount by consulting each connector's
 * `isAuthorized()`. For a TARGETED injected connector — which is what makes
 * "MetaMask" appear by name — that check is satisfied by `eth_accounts` alone:
 * @wagmi/core's own comment notes the `injected.connected` guard is skipped
 * "if no target is set", so a targeted connector has no such guard. The effect
 * is that anyone whose MetaMask has ever authorised this domain gets silently
 * connected on page load, whether or not they ever pressed Connect here.
 *
 * The two connectors also keep SEPARATE disconnect shims (`metaMask.disconnected`
 * vs `injected.disconnected`), so disconnecting through one leaves the other
 * free to reconnect on the next reload.
 *
 * Rather than fight either behaviour, the app stops delegating the decision:
 * `reconnectOnMount` is off, and we reconnect only when this flag says the user
 * connected here and has not disconnected since. One flag, one meaning, and it
 * is cleared by an explicit disconnect — so "disconnected stays disconnected"
 * no longer depends on which connector's shim key happened to be written.
 */
/**
 * DISTINCT FROM THE FORGE'S KEY ON PURPOSE. Both apps ship to the same origin
 * (tobyworld.app/twpot/ and tobyworld.app/faucet/), so they share one
 * localStorage. Reusing `twpot.wallet.connected` would make connecting on one
 * page silently auto-connect the other, and disconnecting on either would log
 * the visitor out of both — a cross-app coupling with no upside.
 */
const KEY = 'openfaucet.wallet.connected'

function storage(): Storage | null {
  try {
    // Private browsing and blocked third-party storage both throw on access.
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

/** Records that the user connected deliberately, through this app. */
export function rememberConnection(connectorId: string): void {
  try {
    storage()?.setItem(KEY, connectorId)
  } catch {
    /* storage unavailable — reconnect simply will not persist */
  }
}

/** Clears the record. Called on explicit disconnect. */
export function forgetConnection(): void {
  try {
    storage()?.removeItem(KEY)
  } catch {
    /* nothing to do */
  }
}

/** The connector id the user last connected with here, if any. */
export function rememberedConnection(): string | null {
  try {
    return storage()?.getItem(KEY) ?? null
  } catch {
    return null
  }
}
